/**
 * Solid things in the water: quay walls, breakwaters, pier heads.
 *
 * Everything is an axis-aligned box in its own frame, rotated about Y. That is
 * enough to describe a harbour, and reduces the collision test to a clamp.
 */

import { Vector3 } from 'three'

export interface Obstacle {
  x: number
  z: number
  /** Half-extent along the obstacle's local X. */
  halfWidth: number
  /** Half-extent along the obstacle's local Z. */
  halfLength: number
  /** Rotation about Y, radians. */
  rotation: number
}

/**
 * Nearest point on the obstacle's footprint to a world XZ position.
 * `target.y` is left at zero — everything here is a plan-view problem.
 */
export function closestPointOnObstacle(obstacle: Obstacle, px: number, pz: number, target: Vector3): Vector3 {
  const cos = Math.cos(obstacle.rotation)
  const sin = Math.sin(obstacle.rotation)

  const dx = px - obstacle.x
  const dz = pz - obstacle.z

  // World -> obstacle frame. The mesh is rendered with rotation.y = -rotation;
  // this transform MUST be its exact inverse or the collision box swings away
  // from the drawn wall — at the tip of a 400 m breakwater even a small sign
  // error opens a ship-sized gap between the visual and the physics.
  const lx = dx * cos + dz * sin
  const lz = -dx * sin + dz * cos

  const cx = Math.min(Math.max(lx, -obstacle.halfWidth), obstacle.halfWidth)
  const cz = Math.min(Math.max(lz, -obstacle.halfLength), obstacle.halfLength)

  // Obstacle frame -> world.
  target.set(obstacle.x + cx * cos - cz * sin, 0, obstacle.z + cx * sin + cz * cos)
  return target
}

/** True when a world XZ lies inside the footprint, ignoring height. */
export function obstacleContains(obstacle: Obstacle, px: number, pz: number): boolean {
  const cos = Math.cos(obstacle.rotation)
  const sin = Math.sin(obstacle.rotation)
  const dx = px - obstacle.x
  const dz = pz - obstacle.z
  const lx = dx * cos + dz * sin
  const lz = -dx * sin + dz * cos
  return Math.abs(lx) <= obstacle.halfWidth && Math.abs(lz) <= obstacle.halfLength
}
